import React from 'react';
import { FactoryIcon, ClockIcon, ZapIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTranslation } from '../../lib/i18n';
interface AvailabilityDeliveryProps {
  onRequestQuote: () => void;
}
export function AvailabilityDelivery({
  onRequestQuote
}: AvailabilityDeliveryProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  return <section className="py-20 bg-dark-bg border-y border-dark-border" data-id="element-152">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-153">
        <div className="text-center mb-10" data-id="element-154">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3" data-id="element-155">
            {en ? <>
                Availability and{' '}
                <span className="text-brand-yellow" data-id="element-156">delivery</span>
              </> : ru ? <>
                Наличие и <span className="text-brand-yellow" data-id="element-157">доставка</span>
              </> : <>
                Dostupnost i <span className="text-brand-yellow" data-id="element-158">isporuka</span>
              </>}
          </h2>
        </div>

        <div className="bg-dark-surface border border-dark-border rounded-industrial p-6 md:p-8 mb-8" data-id="element-159">
          <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-160">
            {en ? <>
                D-TECH machines are delivered{' '}
                <span className="text-white font-semibold" data-id="element-161">
                  directly from production
                </span>{' '}
                — this guarantees maximum battery capacity and full motor power
                on every unit. Due to high demand, stock availability varies:
                for models not currently in stock the standard lead time is{' '}
                <span className="text-brand-yellow font-bold" data-id="element-162">4–8 weeks</span>.
              </> : ru ? <>
                Машины D-TECH поставляются{' '}
                <span className="text-white font-semibold" data-id="element-163">
                  напрямую с производства
                </span>{' '}
                — это гарантирует максимальную ёмкость аккумулятора и полную
                мощность мотора для каждой единицы. Из-за высокого спроса
                наличие на складе меняется: для моделей, которых нет в наличии,
                стандартный срок поставки составляет{' '}
                <span className="text-brand-yellow font-bold" data-id="element-164">4–8 недель</span>.
              </> : <>
                D-TECH mašine isporučujemo{' '}
                <span className="text-white font-semibold" data-id="element-165">
                  direktno iz proizvodnje
                </span>{' '}
                — to nam garantuje maksimalni kapacitet baterije i punu snagu
                motora kod svake jedinice. Zbog velike potražnje stanje na
                lageru se menja: za modele koji nisu na stanju standardni rok je{' '}
                <span className="text-brand-yellow font-bold" data-id="element-166">4–8 nedelja</span>
                .
              </>}
          </p>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mt-4" data-id="element-167">
            {en ? <>
                Contact us for current availability, reservations or a{' '}
                <span className="text-white font-semibold" data-id="element-168">
                  free demonstration at your location
                </span>
                .
              </> : ru ? <>
                Свяжитесь с нами для уточнения наличия, бронирования или{' '}
                <span className="text-white font-semibold" data-id="element-169">
                  бесплатной демонстрации на вашей территории
                </span>
                .
              </> : <>
                Kontaktirajte nas za trenutno stanje, rezervaciju ili{' '}
                <span className="text-white font-semibold" data-id="element-170">
                  besplatnu demonstraciju na vašoj lokaciji
                </span>
                .
              </>}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-171">
          <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/60 rounded-industrial p-4" data-id="element-172">
            <FactoryIcon className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-173" />
            <div data-id="element-174">
              <p className="text-white font-semibold text-sm mb-1" data-id="element-175">
                {en ? 'Direct from production' : ru ? 'Напрямую с производства' : 'Direktno iz proizvodnje'}
              </p>
              <p className="text-gray-400 text-xs" data-id="element-176">
                {en ? 'Maximum battery capacity, full motor power.' : ru ? 'Максимальная ёмкость аккумулятора, полная мощность мотора.' : 'Maksimalni kapacitet baterije, puna snaga motora.'}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/60 rounded-industrial p-4" data-id="element-177">
            <ClockIcon className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-178" />
            <div data-id="element-179">
              <p className="text-white font-semibold text-sm mb-1" data-id="element-180">
                {en ? '4–8 weeks lead time' : ru ? 'Срок поставки 4–8 недель' : '4–8 nedelja isporuka'}
              </p>
              <p className="text-gray-400 text-xs" data-id="element-181">
                {en ? 'For models not currently in stock.' : ru ? 'Для моделей, которых нет в наличии.' : 'Za modele koji nisu na stanju.'}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/60 rounded-industrial p-4" data-id="element-182">
            <ZapIcon className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-183" />
            <div data-id="element-184">
              <p className="text-white font-semibold text-sm mb-1" data-id="element-185">
                {en ? 'Free demo' : ru ? 'Бесплатная демонстрация' : 'Besplatna demo'}
              </p>
              <p className="text-gray-400 text-xs" data-id="element-186">
                {en ? 'On your location, by appointment.' : ru ? 'На вашей территории, по договоренности.' : 'Na vašoj lokaciji, po dogovoru.'}
              </p>
            </div>
          </div>
        </div>

        <div className="text-center" data-id="element-187">
          <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-188">
            {en ? 'Check availability' : ru ? 'Проверить наличие' : 'Proverite dostupnost'}
          </Button>
        </div>
      </div>
    </section>;
}